import { useTranslation } from "react-i18next";

import Header from "common/components/Header/Header";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import FilteredList from "common/components/Table/FilteredList";
import useItem from "common/hooks/useItem";
import AlertsContext from "common/providers/alerts";
import {
  ArchiveBoxIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import useRouter from "common/hooks/use-router";
import { ImportItemStatus, ImportType } from "types/Import";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import fetchJSON from "common/utils/fetchJSON";
import { Animal } from "types/Animal";
import ImportDetailItem from "./ImportDetailItem";

function ImportDetailList() {
  const { t } = useTranslation();
  const router = useRouter();
  const { setAlert } = useContext(AlertsContext);

  // const { user: currentUser } = useAuth();
  const id = router.query.id as string;

  const { item, error, isFetching } = useItem<any>("imports", id, {
    listenToEvents: [
      "import_file_imported",
      "import_file_new_data",
      "import_file_imported_with_error",
      "import_file_updated",
    ],
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    if (
      item &&
      item.parsedContent &&
      item.parsedContent.data &&
      item.parsedContent.data.length > 0
    ) {
      setItems(item.parsedContent.data);
    }
  }, [item]);

  const itemsCount = useMemo(() => items.length, [items.length]);
  const itemsPerPage = 100000000;
  const pageCount = useMemo(
    () => Math.ceil(itemsCount / itemsPerPage),
    [itemsCount]
  );

  const [pageIndex, setPageIndex] = useState<number>(0);
  const columns = useMemo(
    () => [
      {
        Header: t("forms.name"),
        filterName: "name$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-[2]",
      },
    ],
    [t]
  );

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: null,
        icon: ListBulletIcon,
        color: "secondary",
      },
      {
        id: "imported",
        name: t("import.imported"),
        count: null,
        icon: CheckIcon,
        color: "green",
      },
      {
        id: "in_progress",
        name: t("import.in_progress"),
        count: null,
        icon: ClipboardDocumentCheckIcon,
        color: "blue",
      },
      {
        id: "to_archive",
        name: t("import.to_archive"),
        count: null,
        icon: ExclamationCircleIcon,
        color: "yellow",
      },
      {
        id: "archived",
        name: t("import.archived"),
        count: null,
        icon: ArchiveBoxIcon,
        color: "slate",
      },
      {
        id: "error",
        name: t("import.error"),
        count: null,
        icon: ExclamationTriangleIcon,
        color: "red",
      },
      {
        id: "canceled",
        name: t("import.canceled"),
        count: null,
        icon: XMarkIcon,
        color: "primary",
      },
    ];
  }, [t]);

  const [filters, setFilter] = useState<any>({ status: "" });

  useEffect(() => {
    if (
      item &&
      item.parsedContent &&
      item.parsedContent.data &&
      item.parsedContent.data.length > 0
    ) {
      let filteredItems = item.parsedContent.data;
      if (filters.status) {
        filteredItems = filteredItems.filter(
          (data: any) => data.status === filters.status
        );
      }
      setItems(filteredItems);
    }
  }, [item, filters]);

  const onCancelImport = useCallback(
    async (importItem: any) => {
      try {
        const updatedData = item?.parsedContent?.data?.map((data: any) =>
          data.localId === importItem.localId
            ? { ...data, status: ImportItemStatus.CANCELED }
            : data
        );

        await fetchJSON({
          url: `imports/update-status`,
          method: "PUT",
          payload: {
            data: {
              ...item,
              parsedContent: {
                ...importItem?.parsedContent,
                data: updatedData,
              },
            },
          },
        });
      } catch (err) {
        console.error("Update import status error:", err);
      }
    },
    [item]
  );

  const { removeItem } = useItem<Animal>("animals", "");

  const handleArchiveData = useCallback(
    async (selectedItem: any) => {
      try {
        if (item.importType === ImportType.ZIMS) {
          const updatedData = item?.parsedContent?.data?.map((data: any) =>
            data.id === selectedItem.id
              ? { ...data, status: ImportItemStatus.ARCHIVED }
              : data
          );

          await fetchJSON({
            url: `imports/update-status`,
            method: "PUT",
            payload: {
              data: {
                ...item,
                parsedContent: {
                  ...selectedItem?.parsedContent,
                  data: updatedData,
                },
              },
            },
          });

          await removeItem(selectedItem.id);
        }
      } catch (err) {
        console.error("Erreur lors de l'import du fichier : ", err);
      }
    },
    [item, removeItem]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.importDetails")}
        subtitle={t("titleLegends.importDetails")}
        Icon={DocumentMagnifyingGlassIcon}
        canGoBack
        filters={filters}
        setFilter={(filter, value) => setFilter(() => ({ [filter]: value }))}
        filterName="status"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />
      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        noHeader
        isFetching={isFetching}
        pageCount={pageCount}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        onDelete={handleArchiveData}
        RenderItem={(props: any) => (
          <ImportDetailItem
            type={item?.importType}
            onCancelImport={onCancelImport}
            {...props}
          />
        )}
        onNavigate={() => {}}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
        filters={{}}
        setFilter={() => {}}
      />
    </div>
  );
}

export default ImportDetailList;
